import React, { createContext, useContext, useEffect, useState } from "react";
import client, { apiError } from "@/lib/api";
import { User } from "@/lib/types";

interface AuthCtx {
  user: User | null | undefined; // undefined = checking, null = not authed
  passwordExpired: boolean;
  login: (email: string, password: string, mfaCode?: string) => Promise<void>;
  logout: () => void;
  clearPasswordExpired: () => void;
}

const AuthContext = createContext<AuthCtx>({} as AuthCtx);

export const useAuth = () => useContext(AuthContext);

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<User | null | undefined>(undefined);
  const [passwordExpired, setPasswordExpired] = useState(false);

  useEffect(() => {
    const token = localStorage.getItem("sp_token");
    if (!token) {
      setUser(null);
      return;
    }
    client
      .get("/auth/me")
      .then((res) => {
        setUser(res.data);
        setPasswordExpired(!!res.data.password_expired);
      })
      .catch(() => {
        localStorage.removeItem("sp_token");
        setUser(null);
      });
  }, []);

  const login = async (email: string, password: string, mfaCode?: string) => {
    try {
      const res = await client.post("/auth/login", { email, password, mfa_code: mfaCode });
      localStorage.setItem("sp_token", res.data.access_token);
      setUser(res.data.user);
      setPasswordExpired(!!res.data.password_expired);
    } catch (err) {
      throw new Error(apiError(err));
    }
  };

  const logout = () => {
    localStorage.removeItem("sp_token");
    setUser(null);
    setPasswordExpired(false);
    window.location.href = "/login";
  };

  const clearPasswordExpired = () => setPasswordExpired(false);

  return (
    <AuthContext.Provider value={{ user, passwordExpired, login, logout, clearPasswordExpired }}>
      {children}
    </AuthContext.Provider>
  );
};
